import { useCallback } from 'preact/hooks';
import { CalendarRange } from 'lucide-preact';
import '../../css/monthRangeSlider.css';

export interface MonthOption {
  id: string;
  label: string;
}

export interface MonthRangeValue {
  startId: string;
  endId: string;
}

const EMPTY_MESSAGE = 'No data available';

export interface MonthRangeSliderProps {
  /** Months in ascending order (earliest first) */
  months: MonthOption[];
  value: MonthRangeValue;
  onChange: (startId: string, endId: string) => void;
  className?: string;
}

export const MonthRangeSlider = ({
  months,
  value,
  onChange,
  className = '',
}: MonthRangeSliderProps) => {
  const maxIndex = Math.max(0, months.length - 1);
  const startIndex = months.findIndex((m) => m.id === value.startId);
  const endIndex = months.findIndex((m) => m.id === value.endId);
  const safeStart = startIndex < 0 ? 0 : Math.min(startIndex, maxIndex);
  const safeEnd = endIndex < 0 ? maxIndex : Math.min(endIndex, maxIndex);
  const startIdx = Math.min(safeStart, safeEnd);
  const endIdx = Math.max(safeStart, safeEnd);

  const handleStartChange = useCallback(
    (e: Event) => {
      const target = e.target as HTMLInputElement;
      const newStart = parseInt(target.value, 10);
      const clamped = Math.min(newStart, endIdx);
      const startId = months[clamped]?.id ?? value.startId;
      const endId = months[endIdx]?.id ?? value.endId;
      onChange(startId, endId);
    },
    [months, endIdx, value.startId, value.endId, onChange]
  );

  const handleEndChange = useCallback(
    (e: Event) => {
      const target = e.target as HTMLInputElement;
      const newEnd = parseInt(target.value, 10);
      const clamped = Math.max(newEnd, startIdx);
      const startId = months[startIdx]?.id ?? value.startId;
      const endId = months[clamped]?.id ?? value.endId;
      onChange(startId, endId);
    },
    [months, startIdx, value.startId, value.endId, onChange]
  );

  if (months.length === 0) {
    return (
      <div className={`month-range-slider month-range-slider--empty ${className}`}>
        <p className="month-range-slider__message">{EMPTY_MESSAGE}</p>
      </div>
    );
  }

  const startLabel = months[startIdx]?.label ?? value.startId;
  const endLabel = months[endIdx]?.label ?? value.endId;
  const fromPct = maxIndex === 0 ? 0 : (startIdx / maxIndex) * 100;
  const toPct = maxIndex === 0 ? 100 : (endIdx / maxIndex) * 100;

  return (
    <div
      className={`month-range-slider ${className}`}
      style={
        {
          '--range-from-pct': `${fromPct}%`,
          '--range-to-pct': `${toPct}%`,
        } as Record<string, string>
      }
    >
      <CalendarRange size={12} className="month-range-slider__icon" aria-hidden="true" />
      <span className="month-range-slider__chip month-range-slider__chip--start">
        {startLabel}
      </span>

      <div className="month-range-slider__track-wrap">
        <div className="month-range-slider__track" aria-hidden="true" />
        <div className="month-range-slider__fill" aria-hidden="true" />
        <input
          type="range"
          min={0}
          max={maxIndex}
          step={1}
          value={startIdx}
          onInput={handleStartChange}
          className="month-range-slider__input month-range-slider__input--start"
          aria-label="Start month"
          aria-valuetext={startLabel}
        />
        <input
          type="range"
          min={0}
          max={maxIndex}
          step={1}
          value={endIdx}
          onInput={handleEndChange}
          className="month-range-slider__input month-range-slider__input--end"
          aria-label="End month"
          aria-valuetext={endLabel}
        />
      </div>

      <span className="month-range-slider__chip month-range-slider__chip--end">
        {endLabel}
      </span>
    </div>
  );
};
